import React, { useState, useEffect, useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { normalizeAttendee } from "../utils/attendee-profile";
import { UserContext, USER_ROLES } from "./UserProvider";
import {
  APP_EVENT_ID as eventId,
  REACT_APP_SERVER_BASE_URL,
} from "../utils/consts";
import { hubConnection as connection } from "signalr-no-jquery";
const MeetingContext = React.createContext({});
const MEETING_HUB = "meetingHub"; 
const ConnectionStateOpen = 1;

const WEBSOCKET_EVENT_TYPES = {
  ReceiveMeetingUpdate: "ReceiveMeetingUpdate",
  ReceiveReceiptionUpdate: "ReceiveReceiptionUpdate",
  GetMeetingAndReceptionDetails: "GetMeetingAndReceptionDetails"
};

const MeetingProvider = (props) => {
  const history = useHistory();
  const [meetingAttendeesList, setMeetingAttendeesList] = useState([]);
  const [receptionAttendeesList, setReceptionAttendeesList] = useState([]);
  const [ismeetingOpened, setIsmeetingOpened] = useState();
  const [isReceiptionOpened, setIsReceiptionOpened] = useState();
  const { userProfile, companyId } = useContext(UserContext);
  const attendeeID = userProfile ? userProfile.AttendeeID : 0;
  const [meetingId, setMeetingId] = useState();
  const [hubConnection, setHubConnection] = useState();
  const [hubProxy, setHubProxy] = useState();

  // Set the hub connection and event listerners.
  useEffect(() => {
    const hubConnection = connection(REACT_APP_SERVER_BASE_URL);
    setHubConnection(hubConnection);
    const hubProxy = hubConnection.createHubProxy(MEETING_HUB);
    setHubProxy(hubProxy);

    // set up event listeners i.e. for incoming "message" event
    hubProxy.on(WEBSOCKET_EVENT_TYPES.ReceiveMeetingUpdate, (meetingReceptionDetails) => { 
      if (meetingReceptionDetails) {
        setIsmeetingOpened(meetingReceptionDetails.meetingOpen);
        setIsReceiptionOpened(meetingReceptionDetails.receptionOpen);
        var meetingAttendeesList = meetingReceptionDetails.currentMeetingAttendees.map(
          (attendee) => normalizeAttendee(attendee)
        );
        setMeetingAttendeesList(meetingAttendeesList);

        var receiptionAttendeesList = meetingReceptionDetails.currentReceptionAttendees.map(
          (attendee) => normalizeAttendee(attendee)
        );
        setReceptionAttendeesList(
          receiptionAttendeesList.sort(function (a, b) {
            return a.Role === b.Role ? 0 : a.Role === USER_ROLES.ADMIN ? -1 : 1;
          })
        );
      }
    }); 

    hubProxy.on(WEBSOCKET_EVENT_TYPES.ReceiveReceiptionUpdate, (meetingReceptionDetails) => {
      if (
        meetingReceptionDetails &&
        history.location.pathname.includes("/reception")
      ) {
        setIsReceiptionOpened(meetingReceptionDetails.receptionOpen);
        var receiptionAttendeesList = meetingReceptionDetails.currentReceptionAttendees.map(
          (attendee) => normalizeAttendee(attendee)
        );
        setReceptionAttendeesList(
          receiptionAttendeesList.sort(function (a, b) {
            return a.Role === b.Role ? 0 : a.Role === USER_ROLES.ADMIN ? -1 : 1;
          })
        );
      }
    });

    return () => {
      hubProxy.off(WEBSOCKET_EVENT_TYPES.ReceiveMeetingUpdate);
      hubProxy.off(WEBSOCKET_EVENT_TYPES.ReceiveReceiptionUpdate);
    };

  }, [history.location.pathname]);

  // Get the meeting id from url parameter and save to state.
  useEffect(() => {
    var meetingIdParm;
    if (history.location.pathname.includes("/reception")) {
      meetingIdParm = history.location.pathname
        .split("/reception")
        .pop()
        .replace("/", "");

      if (meetingIdParm) {
        setMeetingId(meetingIdParm);
      }
    } else if (history.location.pathname.includes("meeting/")) {
      meetingIdParm = history.location.pathname.split("meeting/").pop();
      setMeetingId(meetingIdParm);
    }
  }, [companyId, attendeeID, history.location.pathname]);

  // useCallback function to invoke GetMeetingAndReceptionDetails 
  const MeetingAndReceptionDetailsCallBack = useCallback(() => {
    if (
      companyId &&
      hubProxy &&
      hubProxy.connection.state === ConnectionStateOpen
    ) {
      hubProxy.invoke(WEBSOCKET_EVENT_TYPES.GetMeetingAndReceptionDetails, {
        companyId,
        eventId,
        meetingId,
      });
    }
  }, [companyId, meetingId, hubProxy]);

  // Start hub connection and invoke GetMeetingAndReceptionDetails method so that all other host will be notified
  useEffect(() => {
    if ( hubConnection) {
      hubConnection
        .start()
        .done(function () {
          MeetingAndReceptionDetailsCallBack();
        })
        .fail(function () {
          console.log("Could not connect");
        });
    }
  }, [MeetingAndReceptionDetailsCallBack, hubConnection]);  

  // Will be executed when the URL changes.
  //When a host joins waiting in receiption and joins the meeting - the URL changes from /receiption to /meeting
  useEffect(() => {
    MeetingAndReceptionDetailsCallBack();
  }, [MeetingAndReceptionDetailsCallBack, history.location.pathname]);

  // when the receiption opened, invoke GetMeetingAndReceptionDetails to get the receiption attendiees updated for all clients
  useEffect(() => {
    if (
      history.location.pathname.includes("/reception") &&
      isReceiptionOpened
    ) {
      MeetingAndReceptionDetailsCallBack();
    }
  }, [
    MeetingAndReceptionDetailsCallBack,
    history.location.pathname,
    isReceiptionOpened,
  ]);

  return (
    <MeetingContext.Provider
      value={{
        meetingAttendeesList,
        receptionAttendeesList,
        ismeetingOpened,
        isReceiptionOpened,
        updateMeetingAndReceptionDetails: MeetingAndReceptionDetailsCallBack
      }}
    >
      {props.children}
    </MeetingContext.Provider>
  );
};

export { MeetingContext, MeetingProvider };
